import React from "react";

/**
 * Marks a schedule that is outranked by a higher-priority one in the same slot.
 *
 * Scheduling over an existing schedule does not delete it — the new schedule is
 * given a higher priority and the player keeps only the highest, so the older
 * row stays live underneath and resumes the moment the winner is deleted.
 *
 * The portal deliberately keeps these rows visible: hiding them would make the
 * delete button look like it conjures a schedule out of nowhere. The badge is
 * what tells an operator the row is real but not currently playing.
 *
 * An override that covers only part of a schedule is a different story: a
 * 12:00-17:00 block with a 13:00-15:00 override dropped into it still plays
 * 12:00-13:00 and 15:00-17:00. Passing `windows` names the hours that are
 * actually lost and keeps the row ungreyed, because the schedule is still on
 * air for the rest of its span.
 *
 * Playback is resolved by priority on the device, not here; this is only the
 * signal.
 */
const formatWindow = (window: string) =>
  window
    .split("-")
    .map((time) => time.split(":").slice(0, 2).join(":"))
    .join("–");

const SupersededBadge: React.FC<{
  className?: string;
  windows?: string[];
}> = ({ className = "", windows }) => {
  const isPartial = !!windows?.length;
  const label = isPartial
    ? `Superseded ${windows!.map(formatWindow).join(", ")}`
    : "Superseded";

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        isPartial
          ? "bg-amber-100 text-amber-700"
          : "bg-slate-200 text-slate-600"
      } ${className}`}
      title={
        isPartial
          ? "Overwritten by a newer schedule for part of its window — it still plays outside these hours, and resumes in full if the newer schedule is deleted."
          : "Overwritten by a newer schedule in this slot — not playing. It resumes if the newer schedule is deleted."
      }
    >
      {label}
    </span>
  );
};

export default SupersededBadge;
